import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import EstadoBadge from "../components/EstadoBadge";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { Plus, Search, X, SlidersHorizontal } from "lucide-react";

// Normaliza: minúsculas + sin acentos
const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Construye un "haystack" con todos los campos buscables del convenio
const buildHaystack = (c) =>
  normalize(
    [
      c.institucion_nombre,
      c.institucion_pais,
      c.institucion_ciudad,
      c.institucion_tipo,
      c.tipo,
      c.vigencia_tipo,
      c.observaciones,
      ...(c.facultad_nombres || []),
      ...(c.unidad_nombres || []),
      ...(c.areas_tematicas || []),
    ]
      .filter(Boolean)
      .join(" ")
  );

const VIGENCIA_OPTS_KEYS = [
  { v: "fijo", labelKey: "convenios.vig_fija" },
  { v: "indefinido", labelKey: "convenios.indefinido" },
  { v: "auto_renovable", labelKey: "convenios.vig_auto" },
];

const TIPO_OPTS_KEYS = [
  { v: "marco", labelKey: "convenios.tipo_marco" },
  { v: "especifico", labelKey: "convenios.tipo_esp" },
];

export default function Convenios() {
  const { user } = useAuth();
  const { config } = useConfig();
  const { t } = useTranslation();
  const [convenios, setConvenios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState("all");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    pais: "",
    tipo: "",
    vigencia: "",
    facultad: "",
  });

  useEffect(() => {
    api.get("/convenios").then((r) => setConvenios(r.data)).finally(() => setLoading(false));
  }, []);

  // Indexa convenios con haystack precomputado
  const indexed = useMemo(
    () => convenios.map((c) => ({ ...c, _haystack: buildHaystack(c) })),
    [convenios]
  );

  // Opciones únicas para filtros
  const paises = useMemo(
    () => Array.from(new Set(convenios.map((c) => c.institucion_pais).filter(Boolean))).sort(),
    [convenios]
  );
  const facultadesUnique = useMemo(() => {
    const map = new Map();
    convenios.forEach((c) => {
      (c.facultad_ids || []).forEach((id, i) => {
        const nombre = (c.facultad_nombres || [])[i];
        if (id && nombre && !map.has(id)) map.set(id, nombre);
      });
    });
    return Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [convenios]);

  const counts = useMemo(() => {
    return {
      all: convenios.length,
      activo: convenios.filter((c) => c.estado === "activo").length,
      por_vencer: convenios.filter((c) => c.estado === "por_vencer").length,
      vencido: convenios.filter((c) => c.estado === "vencido").length,
    };
  }, [convenios]);

  // Búsqueda: tokens separados por espacios. Cada token debe aparecer en haystack (AND)
  const searchTokens = useMemo(
    () => normalize(search).split(/\s+/).filter(Boolean),
    [search]
  );

  const filtered = useMemo(() => {
    return indexed.filter((c) => {
      if (estado !== "all" && c.estado !== estado) return false;
      if (filters.pais && c.institucion_pais !== filters.pais) return false;
      if (filters.tipo && c.tipo !== filters.tipo) return false;
      if (filters.vigencia && (c.vigencia_tipo || "fijo") !== filters.vigencia) return false;
      if (filters.facultad && !(c.facultad_ids || []).includes(filters.facultad)) return false;
      if (searchTokens.length > 0) {
        if (!searchTokens.every((t) => c._haystack.includes(t))) return false;
      }
      return true;
    });
  }, [indexed, estado, filters, searchTokens]);

  const activeFilterCount =
    (filters.pais ? 1 : 0) +
    (filters.tipo ? 1 : 0) +
    (filters.vigencia ? 1 : 0) +
    (filters.facultad ? 1 : 0);

  const clearFilters = () => setFilters({ pais: "", tipo: "", vigencia: "", facultad: "" });
  const clearAll = () => {
    clearFilters();
    setSearch("");
    setEstado("all");
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-12">
          <div>
            <div className="section-marker">
              {user?.role === "facultad" ? t("convenios.kicker_mis") : t("convenios.kicker")}
            </div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
              {t("convenios.page_title")}<span className="serif-em text-zinc-900">.</span>
            </h1>
          </div>
          {user?.role === "admin" && (
            <Link
              to="/convenios/new"
              className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
              data-testid="btn-new-convenio"
            >
              <Plus size={18} strokeWidth={1.5} /> {t("convenios.btn_new")}
            </Link>
          )}
        </div>

        {/* Search bar */}
        <div className="mb-4">
          <div className="relative">
            <Search size={18} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("convenios.search_placeholder")}
              className="w-full border border-zinc-300 pl-11 pr-10 py-3 text-base focus:outline-none focus:ring-2 focus:ring-zinc-950"
              data-testid="search-convenios"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-950 p-1"
                aria-label={t("common.search")}
                data-testid="search-clear"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            )}
          </div>
          {searchTokens.length > 1 && (
            <div className="text-xs text-zinc-500 mt-2">
              {t("convenios.searching_tokens", { n: searchTokens.length })}{" "}
              {searchTokens.map((tok, i) => (
                <span key={i} className="inline-block border border-zinc-200 px-1.5 py-0.5 mx-0.5 text-[11px]">{tok}</span>
              ))}
            </div>
          )}
        </div>

        {/* Estado chips + filters toggle */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
          {[
            { k: "all", label: t("common.todos"), count: counts.all, dot: null },
            { k: "activo", label: t("common.activos"), count: counts.activo, dot: "#047857" },
            { k: "por_vencer", label: t("common.por_vencer"), count: counts.por_vencer, dot: "#d97706" },
            { k: "vencido", label: t("common.vencidos"), count: counts.vencido, dot: "#b91c1c" },
          ].map((opt) => (
            <button
              key={opt.k}
              onClick={() => setEstado(opt.k)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 border text-sm transition-colors ${
                estado === opt.k
                  ? "border-zinc-950 bg-zinc-950 text-white"
                  : "border-zinc-200 bg-white hover:bg-zinc-50"
              }`}
              data-testid={`filter-${opt.k}`}
            >
              {opt.dot && (
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: opt.dot }} aria-hidden></span>
              )}
              <span>{opt.label}</span>
              <span className={estado === opt.k ? "text-zinc-300" : "text-zinc-400"}>({opt.count})</span>
            </button>
          ))}
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`ml-auto inline-flex items-center gap-2 px-3 sm:px-4 py-2 border text-sm transition-colors ${
              showFilters || activeFilterCount > 0
                ? "border-zinc-950 bg-zinc-50 text-zinc-950"
                : "border-zinc-200 hover:bg-zinc-50"
            }`}
            data-testid="btn-toggle-filters"
          >
            <SlidersHorizontal size={14} strokeWidth={1.5} />
            {t("common.filters")}
            {activeFilterCount > 0 && (
              <span className="bg-zinc-950 text-white rounded-full px-1.5 text-[11px] font-medium">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Expandable filters panel */}
        {showFilters && (
          <div className="border border-zinc-200 bg-zinc-50 p-4 sm:p-5 mb-4" data-testid="filters-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("common.pais")}</label>
                <select value={filters.pais} onChange={(e) => setFilters((f) => ({ ...f, pais: e.target.value }))} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm" data-testid="filter-pais">
                  <option value="">{t("common.todos")}</option>
                  {paises.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("common.tipo")}</label>
                <select value={filters.tipo} onChange={(e) => setFilters((f) => ({ ...f, tipo: e.target.value }))} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm" data-testid="filter-tipo">
                  <option value="">{t("common.todos")}</option>
                  {TIPO_OPTS_KEYS.map((opt) => (<option key={opt.v} value={opt.v}>{t(opt.labelKey)}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("common.vigencia")}</label>
                <select value={filters.vigencia} onChange={(e) => setFilters((f) => ({ ...f, vigencia: e.target.value }))} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm" data-testid="filter-vigencia">
                  <option value="">{t("common.todos")}</option>
                  {VIGENCIA_OPTS_KEYS.map((opt) => (<option key={opt.v} value={opt.v}>{t(opt.labelKey)}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {config.facultad_label_singular || t("convenios.col_facultad")}
                </label>
                <select value={filters.facultad} onChange={(e) => setFilters((f) => ({ ...f, facultad: e.target.value }))} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm" data-testid="filter-facultad">
                  <option value="">{t("common.todos")}</option>
                  {facultadesUnique.map((f) => (<option key={f.id} value={f.id}>{f.nombre}</option>))}
                </select>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center justify-between">
                <span className="text-xs text-zinc-500">
                  {t("convenios.filters_active", { n: activeFilterCount })}
                </span>
                <button onClick={clearFilters} className="text-xs underline text-zinc-700 hover:text-zinc-950" data-testid="clear-filters">
                  {t("convenios.clear_filters")}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Result summary */}
        <div className="flex items-center justify-between pb-4 mb-2 border-b border-zinc-200">
          <div className="text-sm text-zinc-500" data-testid="results-count">
            <strong className="text-zinc-950">{filtered.length}</strong> {t("convenios.of_label")} {convenios.length} {convenios.length === 1 ? t("convenios.results_one") : t("convenios.results_other")}
          </div>
          {(search || estado !== "all" || activeFilterCount > 0) && (
            <button onClick={clearAll} className="text-xs underline text-zinc-500 hover:text-zinc-950" data-testid="clear-all">
              {t("convenios.clear_all")}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-zinc-500 py-8">{t("common.loading")}</div>
        ) : (
          <div className="border-t-2 border-zinc-950 overflow-x-auto">
            <table className="w-full min-w-[760px]" data-testid="convenios-table">
              <thead>
                <tr>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("convenios.col_institucion")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("convenios.col_pais")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("convenios.col_tipo")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("convenios.col_facultad")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("convenios.col_vencimiento")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("convenios.col_estado")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="py-16 text-center text-zinc-500">
                    {convenios.length === 0 ? t("convenios.empty") : t("convenios.no_match")}
                  </td></tr>
                )}
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-zinc-100 row-hover cursor-pointer" onClick={() => (window.location.href = `/convenios/${c.id}`)} data-testid={`row-${c.id}`}>
                    <td className="py-5">
                      <Link to={`/convenios/${c.id}`} className="font-medium hover:underline">
                        {c.institucion_nombre}
                      </Link>
                      {c.institucion_ciudad && <div className="text-xs text-zinc-500">{c.institucion_ciudad}</div>}
                    </td>
                    <td className="py-5 text-zinc-600">{c.institucion_pais}</td>
                    <td className="py-5 text-zinc-600 capitalize">{c.tipo}</td>
                    <td className="py-5 text-zinc-600">{(c.facultad_nombres || []).join(", ") || "—"}</td>
                    <td className="py-5 text-zinc-600 tabular-nums">
                      {c.vigencia_tipo === "indefinido" ? (
                        <span className="text-zinc-400">{t("convenios.indefinido")}</span>
                      ) : (
                        <>
                          {c.no_renovacion_iniciada ? c.no_renovacion_fecha_efectiva : c.fecha_vencimiento}
                          {c.vigencia_tipo === "auto_renovable" && !c.no_renovacion_iniciada && (
                            <span className="ml-1 text-xs text-zinc-400">↻</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="py-5"><EstadoBadge estado={c.estado} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
